import React, { useState, useEffect, useMemo } from 'react';
import { useAppContext } from '../App';
import { formatRelativeTime, filterByTimeRange } from '../utils/location';
import { TimeFilter } from '../types';
import styles from './AdminPage.module.css';

const FONT_OPTIONS = [
  { value: 'The Seasons', label: 'The Seasons (Default)' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Verdana', label: 'Verdana' },
  { value: 'Trebuchet MS', label: 'Trebuchet MS' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Impact', label: 'Impact' },
];

type TabType = 'settings' | 'branding' | 'appearance' | 'content' | 'reports';

const AdminPage: React.FC = () => {
  const { 
    isAdmin, login, logout, 
    settings, updateSettings,
    poopReports, activePoopReports, updatePoopReport, deletePoopReport 
  } = useAppContext();
  
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('settings');
  const [contentSubTab, setContentSubTab] = useState<'mission' | 'privacy' | 'terms'>('mission');
  
  // Settings form
  const [expiryDays, setExpiryDays] = useState(settings.expiryDays);
  const [blurMeters, setBlurMeters] = useState(settings.blurMeters);
  const [contactName, setContactName] = useState(settings.contactName || '');
  const [contactEmail, setContactEmail] = useState(settings.contactEmail || '');
  const [settingsSaved, setSettingsSaved] = useState(false);

  // Branding form
  const [logoText, setLogoText] = useState(settings.logoText || 'Pin the Poo');
  const [logoFont, setLogoFont] = useState(settings.logoFont || 'The Seasons');
  const [logoFontSize, setLogoFontSize] = useState(settings.logoFontSize || 1.5);
  const [logoColor, setLogoColor] = useState(settings.logoColor || '#ffffff');
  const [tagline, setTagline] = useState(settings.tagline || '');
  const [brandingSaved, setBrandingSaved] = useState(false);

  // Appearance form
  const [headerBgColor, setHeaderBgColor] = useState(settings.headerBgColor || '#9a3612');
  const [headerTextColor, setHeaderTextColor] = useState(settings.headerTextColor || '#ffffff');
  const [footerBgColor, setFooterBgColor] = useState(settings.footerBgColor || '#9a3612');
  const [footerTextColor, setFooterTextColor] = useState(settings.footerTextColor || '#ffffff');
  const [appearanceSaved, setAppearanceSaved] = useState(false);

  // Content forms
  const [missionTitle, setMissionTitle] = useState(settings.missionTitle || '');
  const [missionContent, setMissionContent] = useState(settings.missionContent || '');
  const [privacyContent, setPrivacyContent] = useState(settings.privacyContent || '');
  const [termsContent, setTermsContent] = useState(settings.termsContent || '');
  const [contentSaved, setContentSaved] = useState(false);

  // Export state
  const [exportFilter, setExportFilter] = useState<TimeFilter | 'all'>('all');

  // Filtered reports for export
  const filteredForExport = useMemo(() => {
    if (exportFilter === 'all') return poopReports;
    return filterByTimeRange(poopReports, exportFilter);
  }, [poopReports, exportFilter]);

  useEffect(() => {
    setExpiryDays(settings.expiryDays);
    setBlurMeters(settings.blurMeters);
    setContactName(settings.contactName || '');
    setContactEmail(settings.contactEmail || '');
    setHeaderBgColor(settings.headerBgColor || '#9a3612');
    setHeaderTextColor(settings.headerTextColor || '#ffffff');
    setFooterBgColor(settings.footerBgColor || '#9a3612');
    setFooterTextColor(settings.footerTextColor || '#ffffff');
    setLogoText(settings.logoText || 'Pin the Poo');
    setLogoFont(settings.logoFont || 'The Seasons');
    setLogoFontSize(settings.logoFontSize || 1.5);
    setLogoColor(settings.logoColor || '#ffffff');
    setTagline(settings.tagline || '');
    setMissionTitle(settings.missionTitle || '');
    setMissionContent(settings.missionContent || '');
    setPrivacyContent(settings.privacyContent || '');
    setTermsContent(settings.termsContent || '');
  }, [settings]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (login(password)) {
      setPassword('');
      setError('');
    } else {
      setError('Invalid password');
    }
  };

  const handleExportCSV = () => {
    if (filteredForExport.length === 0) {
      alert('No data to export');
      return;
    }

    // Create CSV content
    const headers = ['ID', 'Latitude', 'Longitude', 'Geohash', 'Category', 'Date', 'Time', 'Status', 'Held'];
    const rows = filteredForExport.map(report => {
      const date = new Date(report.createdAt);
      const isActive = activePoopReports.some(r => r.id === report.id);
      return [
        report.id,
        report.lat.toFixed(6),
        report.lng.toFixed(6),
        report.geohash || '',
        report.category === 'bagged' ? 'Poo bag left' :
        report.category === 'partially-cleaned' ? 'Partially cleared' : 'Not picked up',
        date.toLocaleDateString(),
        date.toLocaleTimeString(),
        isActive ? 'Active' : 'Expired',
        report.hold ? 'Yes' : 'No'
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `pinthepoo-reports-${exportFilter}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSaveSettings = () => {
    updateSettings({ expiryDays, blurMeters, contactName, contactEmail });
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2000);
  };

  const handleSaveBranding = () => {
    updateSettings({ logoText, logoFont, logoFontSize, logoColor, tagline });
    setBrandingSaved(true);
    setTimeout(() => setBrandingSaved(false), 2000);
  };

  const handleSaveAppearance = () => {
    updateSettings({ headerBgColor, headerTextColor, footerBgColor, footerTextColor });
    setAppearanceSaved(true);
    setTimeout(() => setAppearanceSaved(false), 2000);
  };

  const handleSaveContent = () => {
    updateSettings({ missionTitle, missionContent, privacyContent, termsContent });
    setContentSaved(true);
    setTimeout(() => setContentSaved(false), 2000);
  };

  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <div className={styles.loginCard}>
          <div className={styles.loginHeader}>
            <span className={styles.icon}>🔐</span>
            <h1>Admin Login</h1>
            <p>Enter password to access admin panel</p>
          </div>

          <form onSubmit={handleLogin} className={styles.loginForm}>
            {error && <div className={styles.error}>{error}</div>}
            
            <div className={styles.formGroup}>
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                autoFocus
              />
            </div>

            <button type="submit" className="btn btn-primary w-full">
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.adminPanel}>
        <div className={styles.header}>
          <div>
            <h1>Admin Panel</h1>
            <p className={styles.subtitle}>Manage reports and settings</p>
          </div>
          <button onClick={logout} className="btn btn-ghost">
            Logout
          </button>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'settings' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            ⚙️ Settings
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'branding' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('branding')}
          >
            ✏️ Branding
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'appearance' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('appearance')}
          >
            🎨 Colors
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'content' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('content')}
          >
            📄 Content
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'reports' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            📍 Reports ({poopReports.length})
          </button>
        </div>

        <div className={styles.content}>
          {activeTab === 'settings' && (
            <div className={styles.settingsGrid}>
              <div className={styles.settingCard}>
                <h3>📅 Report Expiry</h3>
                <p>How long reports stay visible before auto-expiring</p>
                <div className={styles.settingInput}>
                  <input
                    type="number"
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(Number(e.target.value))}
                    min={1}
                    max={365}
                  />
                  <span>days</span>
                </div>
              </div>

              <div className={styles.settingCard}>
                <h3>📍 Location Blur</h3>
                <p>Random offset added to protect privacy (0 = exact location)</p>
                <div className={styles.settingInput}>
                  <input
                    type="number"
                    value={blurMeters}
                    onChange={(e) => setBlurMeters(Number(e.target.value))}
                    min={0}
                    max={500}
                    step={10}
                  />
                  <span>meters</span>
                </div>
              </div>

              <div className={styles.settingCard}>
                <h3>📧 Contact Info</h3>
                <p>Displayed in footer and legal pages</p>
                <div className={styles.formGroup}>
                  <label>Contact Name</label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Pin the Poo Team"
                  />
                </div>
                <div className={styles.formGroup} style={{ marginTop: '0.75rem' }}>
                  <label>Contact Email</label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="hello@pinthepoo.com"
                  />
                </div>
              </div>

              <div className={styles.buttonRow}>
                <button onClick={handleSaveSettings} className="btn btn-primary">
                  {settingsSaved ? '✓ Saved!' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'branding' && (
            <div className={styles.settingsGrid}>
              <div className={styles.settingCard}>
                <h3>📝 Logo Text</h3>
                <p>The main title displayed in the header</p>
                <div className={styles.formGroup}>
                  <input
                    type="text"
                    value={logoText}
                    onChange={(e) => setLogoText(e.target.value)}
                    placeholder="Pin the Poo"
                    maxLength={30}
                  />
                </div>
              </div>

              <div className={styles.settingCard}>
                <h3>🔤 Logo Font</h3>
                <p>Choose the font for the logo</p>
                <div className={styles.formGroup}>
                  <select
                    value={logoFont}
                    onChange={(e) => setLogoFont(e.target.value)}
                    className={styles.select}
                  >
                    {FONT_OPTIONS.map(font => (
                      <option key={font.value} value={font.value}>{font.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.settingCard}>
                <h3>📏 Logo Size</h3>
                <p>Font size in rem units (1.5 = default)</p>
                <div className={styles.settingInput}>
                  <input
                    type="number"
                    value={logoFontSize}
                    onChange={(e) => setLogoFontSize(Number(e.target.value))}
                    min={1}
                    max={4}
                    step={0.1}
                  />
                  <span>rem</span>
                </div>
              </div>

              <div className={styles.settingCard}>
                <h3>🎨 Logo Color</h3>
                <p>Color of the logo text</p>
                <div className={styles.colorPicker}>
                  <input
                    type="color"
                    value={logoColor}
                    onChange={(e) => setLogoColor(e.target.value)}
                  />
                  <input
                    type="text"
                    value={logoColor}
                    onChange={(e) => setLogoColor(e.target.value)}
                    placeholder="#ffffff"
                  />
                </div>
              </div>

              <div className={styles.settingCard}>
                <h3>💬 Tagline</h3>
                <p>Short text displayed below the logo</p>
                <div className={styles.formGroup}>
                  <input
                    type="text"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder="Keeping our streets clean, one pin at a time"
                    maxLength={60}
                  />
                  <span className={styles.charCount}>{tagline.length}/60</span>
                </div>
              </div>

              <div className={styles.settingCard}>
                <h3>👁️ Preview</h3>
                <div 
                  className={styles.brandingPreview} 
                  style={{ background: headerBgColor }}
                >
                  <span 
                    style={{ 
                      fontFamily: `'${logoFont}', Georgia, serif`,
                      fontSize: `${logoFontSize}rem`,
                      color: logoColor
                    }}
                  >
                    {logoText || 'Pin the Poo'}
                  </span>
                  {tagline && (
                    <span style={{ color: `${headerTextColor}cc`, fontSize: '0.75rem' }}>
                      {tagline}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.buttonRow}>
                <button onClick={handleSaveBranding} className="btn btn-primary">
                  {brandingSaved ? '✓ Saved!' : 'Save Branding'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'appearance' && (
            <div className={styles.settingsGrid}>
              <div className={styles.settingCard}>
                <h3>🔝 Header Colors</h3>
                <p>Customize the header bar appearance</p>
                <div className={styles.colorRow}>
                  <div className={styles.colorInput}>
                    <label>Background</label>
                    <div className={styles.colorPicker}>
                      <input
                        type="color"
                        value={headerBgColor}
                        onChange={(e) => setHeaderBgColor(e.target.value)}
                      />
                      <input
                        type="text"
                        value={headerBgColor}
                        onChange={(e) => setHeaderBgColor(e.target.value)}
                        placeholder="#9a3612"
                      />
                    </div>
                  </div>
                  <div className={styles.colorInput}>
                    <label>Text</label>
                    <div className={styles.colorPicker}>
                      <input
                        type="color"
                        value={headerTextColor}
                        onChange={(e) => setHeaderTextColor(e.target.value)}
                      />
                      <input
                        type="text"
                        value={headerTextColor}
                        onChange={(e) => setHeaderTextColor(e.target.value)}
                        placeholder="#ffffff"
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.colorPreview} style={{ background: headerBgColor, color: headerTextColor }}>
                  Header Preview
                </div>
              </div>

              <div className={styles.settingCard}>
                <h3>🔻 Footer Colors</h3>
                <p>Customize the footer bar appearance</p>
                <div className={styles.colorRow}>
                  <div className={styles.colorInput}>
                    <label>Background</label>
                    <div className={styles.colorPicker}>
                      <input
                        type="color"
                        value={footerBgColor}
                        onChange={(e) => setFooterBgColor(e.target.value)}
                      />
                      <input
                        type="text"
                        value={footerBgColor}
                        onChange={(e) => setFooterBgColor(e.target.value)}
                        placeholder="#9a3612"
                      />
                    </div>
                  </div>
                  <div className={styles.colorInput}>
                    <label>Text</label>
                    <div className={styles.colorPicker}>
                      <input
                        type="color"
                        value={footerTextColor}
                        onChange={(e) => setFooterTextColor(e.target.value)}
                      />
                      <input
                        type="text"
                        value={footerTextColor}
                        onChange={(e) => setFooterTextColor(e.target.value)}
                        placeholder="#ffffff"
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.colorPreview} style={{ background: footerBgColor, color: footerTextColor }}>
                  Footer Preview
                </div>
              </div>

              <div className={styles.buttonRow}>
                <button onClick={handleSaveAppearance} className="btn btn-primary">
                  {appearanceSaved ? '✓ Saved!' : 'Save Colors'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'content' && (
            <div className={styles.contentEditor}>
              <div className={styles.contentTabs}>
                <button
                  className={`${styles.contentTab} ${contentSubTab === 'mission' ? styles.contentTabActive : ''}`}
                  onClick={() => setContentSubTab('mission')}
                >
                  🎯 Mission
                </button>
                <button
                  className={`${styles.contentTab} ${contentSubTab === 'privacy' ? styles.contentTabActive : ''}`}
                  onClick={() => setContentSubTab('privacy')}
                >
                  🔒 Privacy Policy
                </button>
                <button
                  className={`${styles.contentTab} ${contentSubTab === 'terms' ? styles.contentTabActive : ''}`}
                  onClick={() => setContentSubTab('terms')}
                >
                  📜 Terms of Use
                </button>
              </div>

              {contentSubTab === 'mission' && (
                <div className={styles.aboutEditor}>
                  <div className={styles.formGroup}>
                    <label htmlFor="missionTitle">Page Title</label>
                    <input
                      type="text"
                      id="missionTitle"
                      value={missionTitle}
                      onChange={(e) => setMissionTitle(e.target.value)}
                      placeholder="Our Mission"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="missionContent">
                      Content <span className={styles.hint}>(Markdown: **bold**, ## headings, - lists)</span>
                    </label>
                    <textarea
                      id="missionContent"
                      value={missionContent}
                      onChange={(e) => setMissionContent(e.target.value)}
                      rows={15}
                      placeholder="Enter the Mission page content..."
                      className={styles.textarea}
                    />
                  </div>
                </div>
              )}

              {contentSubTab === 'privacy' && (
                <div className={styles.aboutEditor}>
                  <div className={styles.formGroup}>
                    <label htmlFor="privacyContent">
                      Privacy Policy <span className={styles.hint}>(Markdown supported)</span>
                    </label>
                    <textarea
                      id="privacyContent"
                      value={privacyContent}
                      onChange={(e) => setPrivacyContent(e.target.value)}
                      rows={20}
                      placeholder="Enter your Privacy Policy..."
                      className={styles.textarea}
                    />
                  </div>
                </div>
              )}

              {contentSubTab === 'terms' && (
                <div className={styles.aboutEditor}>
                  <div className={styles.formGroup}>
                    <label htmlFor="termsContent">
                      Terms of Use <span className={styles.hint}>(Markdown supported)</span>
                    </label>
                    <textarea
                      id="termsContent"
                      value={termsContent}
                      onChange={(e) => setTermsContent(e.target.value)}
                      rows={20}
                      placeholder="Enter your Terms of Use..."
                      className={styles.textarea}
                    />
                  </div>
                </div>
              )}

              <div className={styles.buttonRow}>
                <button onClick={handleSaveContent} className="btn btn-primary">
                  {contentSaved ? '✓ Saved!' : 'Save Content'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className={styles.reportsSection}>
              {/* Export Controls */}
              <div className={styles.exportControls}>
                <h3>📥 Export Data</h3>
                <div className={styles.exportRow}>
                  <select 
                    value={exportFilter} 
                    onChange={(e) => setExportFilter(e.target.value as TimeFilter | 'all')}
                    className={styles.select}
                  >
                    <option value="all">All Time</option>
                    <option value="this-week">This Week</option>
                    <option value="this-month">This Month</option>
                    <option value="last-month">Last Month</option>
                    <option value="ytd">Year To Date</option>
                    <option value="last-year">Last Year</option>
                  </select>
                  <button onClick={handleExportCSV} className="btn btn-primary">
                    📥 Download CSV ({filteredForExport.length} records)
                  </button>
                </div>
              </div>

              {/* Data Table */}
              <div className={styles.dataTable}>
                {poopReports.length === 0 ? (
                  <div className={styles.emptyState}>No reports yet</div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Location</th>
                        <th>Category</th>
                        <th>Status</th>
                        <th>Geohash</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {poopReports.map(report => (
                        <tr key={report.id}>
                          <td>{formatRelativeTime(report.createdAt)}</td>
                          <td>{report.lat.toFixed(4)}, {report.lng.toFixed(4)}</td>
                          <td>
                            <span className={
                              report.category === 'bagged' ? styles.categoryBagged :
                              report.category === 'partially-cleaned' ? styles.categoryPartial : 
                              styles.categoryUncollected
                            }>
                              {report.category === 'bagged' ? 'Poo bag left' :
                               report.category === 'partially-cleaned' ? 'Partially cleared' : 
                               'Not picked up'}
                            </span>
                          </td>
                          <td>
                            {report.hold && <span className={styles.holdBadge}>HELD</span>}
                            {!report.hold && activePoopReports.includes(report) && <span className={styles.activeBadge}>ACTIVE</span>}
                            {!report.hold && !activePoopReports.includes(report) && <span className={styles.expiredBadge}>EXPIRED</span>}
                          </td>
                          <td><code className={styles.geohashCode}>{report.geohash || '—'}</code></td>
                          <td>
                            <div className={styles.actions}>
                              <button
                                onClick={() => updatePoopReport(report.id, { hold: !report.hold })}
                                className="btn btn-sm btn-ghost"
                              >
                                {report.hold ? '🔓' : '🔒'}
                              </button>
                              <button
                                onClick={() => deletePoopReport(report.id)}
                                className="btn btn-sm btn-danger"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
